import { cn } from "@/lib/utils";
import { Button } from "../ui/button";
import { FC, useState } from "react";
import { DateRange } from "react-day-picker";
import { DateTime } from "luxon";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

import type { ComponentProps } from "react";
import { getDateRangeFromTimePeriod, TimeRangePanel } from "../TimePeriod";
import { TimePeriodsValue, StructureTimePeriodsValue } from "@/config/date";

export type TimePeriodOption = { label: string; value: TimePeriodsValue | StructureTimePeriodsValue };

interface DateRangePickerProps extends Omit<ComponentProps<typeof Calendar>, "selected" | "onSelect" | "mode"> {
	date: DateRange | undefined;
	onChange: (range: DateRange | undefined) => void;
	onClose?: (range: DateRange) => void;
	className?: string;
	timePeriods?: TimePeriodOption[];
}

export const DateRangePicker: FC<DateRangePickerProps> = ({
	className,
	date,
	onChange,
	onClose,
	timePeriods,
	...calendarProps
}) => {
	const [open, setOpen] = useState(false);

	const [timePeriodValue, setTimePeriodValue] = useState<TimePeriodsValue | StructureTimePeriodsValue>();

	const formatDate = (date: Date) => DateTime.fromJSDate(date).toFormat("LLL dd, yyyy");
	const handleOpenChange = (nextOpen: boolean) => {
		setOpen(nextOpen);

		if (date && !nextOpen && typeof onClose === "function") {
			onClose(date);
		}
	};

	const defaultDisabled = (date: Date) => DateTime.fromJSDate(date).startOf("day") > DateTime.now().startOf("day");

	const onTimeRangeValueSelected = (period: TimePeriodsValue | StructureTimePeriodsValue) => {
		setTimePeriodValue(period);
		const range = getDateRangeFromTimePeriod(period);
		if (range && typeof onClose === "function") {
			onChange(range);
			onClose(range);
			setOpen(false);
		}
	};
	return (
		<div className={cn("grid gap-2", className)}>
			<Popover open={open} onOpenChange={handleOpenChange}>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						size="sm"
						className={cn(
							"w-[260px] justify-start text-left font-normal",
							!date && "text-muted-foreground",
						)}
					>
						{date?.from ? (
							date.to ? (
								<>
									{formatDate(date.from)} – {formatDate(date.to)}
								</>
							) : (
								formatDate(date.from)
							)
						) : (
							<span>Pick a date range</span>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-auto p-0 flex" align="start">
					<Calendar
						{...calendarProps}
						mode="range"
						selected={date}
						onSelect={onChange}
						numberOfMonths={2}
						disabled={calendarProps.disabled || defaultDisabled}
					/>
					<TimeRangePanel value={timePeriodValue} onChange={onTimeRangeValueSelected} periods={timePeriods} />
				</PopoverContent>
			</Popover>
		</div>
	);
};
